/**
 * URL Token 免登录认证工具
 * 用于处理第三方系统通过URL参数传递JWT令牌实现免登录
 * 
 * 使用场景：
 * - 第三方页面通过iframe嵌入本系统
 * - URL格式：http://www.fairway.org.cn?token=xxxx
 */

import { getUserInfo } from '@/api/auth'

/**
 * 从URL中提取token参数
 * @returns {string|null} token值，如果不存在则返回null
 */
export function getTokenFromUrl() {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('token')
}

/**
 * 清除URL中的token参数（保持URL整洁，避免token泄露）
 * 使用 history.replaceState 不会触发页面刷新
 */
export function clearTokenFromUrl() {
  const url = new URL(window.location.href)
  if (url.searchParams.has('token')) {
    url.searchParams.delete('token')
    // 使用replaceState避免在历史记录中留下带token的URL
    window.history.replaceState({}, document.title, url.toString())
  }
}

/**
 * 保存token到本地存储
 * @param {string} token JWT令牌
 */
export function saveToken(token) {
  if (token) {
    localStorage.setItem('token', token)
  }
}

/**
 * 获取本地存储的token
 * @returns {string|null} token值
 */
export function getStoredToken() {
  return localStorage.getItem('token')
}

/**
 * 清除本地存储的认证信息
 */
export function clearAuthInfo() {
  localStorage.removeItem('token')
  localStorage.removeItem('username')
  localStorage.removeItem('userId')
  localStorage.removeItem('userRoles')
}

/**
 * 验证token并获取用户信息
 * @param {string} token JWT令牌
 * @returns {Promise<{success: boolean, userInfo?: object, error?: string}>}
 */
export async function validateTokenAndGetUserInfo(token) {
  try {
    // 先临时保存token，以便请求时能携带
    const previousToken = localStorage.getItem('token')
    localStorage.setItem('token', token)
    
    // 调用获取用户信息接口验证token有效性
    const response = await getUserInfo()
    
    if (response.code === 200 && response.data) {
      // token有效，保存用户信息
      const userInfo = response.data
      if (userInfo.username) {
        localStorage.setItem('username', userInfo.username)
      }
      if (userInfo.userId) {
        localStorage.setItem('userId', userInfo.userId)
      }
      if (userInfo.roles) {
        localStorage.setItem('userRoles', JSON.stringify(userInfo.roles))
      }
      
      return {
        success: true,
        userInfo
      }
    } else {
      // token无效，恢复之前的token
      if (previousToken) {
        localStorage.setItem('token', previousToken)
      } else {
        localStorage.removeItem('token')
      }
      return {
        success: false,
        error: response.message || 'Token验证失败'
      }
    }
  } catch (error) {
    // 请求失败，清除token
    localStorage.removeItem('token')
    console.error('Token验证失败:', error)
    return {
      success: false,
      error: error.message || 'Token验证请求失败'
    }
  }
}

/**
 * 处理URL中的token参数进行自动登录
 * 这是主要的入口函数，在路由守卫中调用
 * @returns {Promise<{authenticated: boolean, isNewLogin: boolean, error?: string}>}
 */
export async function handleUrlTokenAuth() {
  const urlToken = getTokenFromUrl()
  
  // 如果URL中没有token参数
  if (!urlToken) {
    const storedToken = getStoredToken()
    return {
      authenticated: !!storedToken,
      isNewLogin: false
    }
  }
  
  console.log('[Token Auth] 检测到URL中的token参数，尝试自动登录...')
  
  // 验证token并获取用户信息
  const result = await validateTokenAndGetUserInfo(urlToken)
  
  if (result.success) {
    console.log('[Token Auth] Token验证成功，用户:', result.userInfo?.username)
    // 清除URL中的token参数
    clearTokenFromUrl()
    return {
      authenticated: true,
      isNewLogin: true,
      userInfo: result.userInfo
    }
  } else {
    console.warn('[Token Auth] Token验证失败:', result.error)
    // 清除URL中的无效token
    clearTokenFromUrl()
    return {
      authenticated: false,
      isNewLogin: false,
      error: result.error
    }
  }
}

/**
 * 检查当前是否在iframe中运行
 * @returns {boolean}
 */
export function isInIframe() {
  try {
    return window.self !== window.top
  } catch (e) {
    // 如果访问window.top抛出异常，说明存在跨域限制，肯定在iframe中
    return true
  }
}

/**
 * 获取父页面的origin（如果在iframe中）
 * 注意：由于跨域限制，可能无法获取
 * @returns {string|null}
 */
export function getParentOrigin() {
  if (!isInIframe()) {
    return null
  }
  try {
    return window.parent.location.origin
  } catch (e) {
    // 跨域情况下无法访问
    return null
  }
}

export default {
  getTokenFromUrl,
  clearTokenFromUrl,
  saveToken,
  getStoredToken,
  clearAuthInfo,
  validateTokenAndGetUserInfo,
  handleUrlTokenAuth,
  isInIframe,
  getParentOrigin
}

